/**
 * L'échelle de prix du commerce.
 *
 * En rayon, les prix ne sont ni continus ni ronds : on écrit 9, 19, 49, 195,
 * 290 — jamais 10, 20, 50, 200 ni 300. Un prix rond se lit comme une
 * estimation approximative ; un prix en 5 ou en 9 se lit comme un prix décidé,
 * et il descend d'une dizaine sans rien coûter. Aucune décimale non plus : la
 * boutique ne rend pas la monnaie sur des centimes toute la journée.
 *
 * Cette échelle sert deux fois : à proposer un prix au moment du dépôt, et à
 * arrondir une estimation calculée sur l'historique — « 143 € » devient
 * « 145 € », pas « 150 € ».
 */
/** Les prix pratiqués en rayon, de 1 € à 9 500 €, en centimes. */
const PALIERS = [
    1, 2, 3, 5, 7, 9,
    12, 15, 19, 25, 29, 35, 39, 45, 49, 59, 69, 79, 89, 95,
    120, 145, 165, 195, 220, 245, 290, 340, 390, 450, 490, 590, 690, 790, 890, 950,
    1200, 1450, 1900, 2400, 2900, 3900, 4900, 5900, 7900, 9500,
].map((euros) => euros * 100);
/** Le palier le plus proche d'un montant — « 143 € » devient « 145 € ». */
export function palierProche(centimes) {
    return PALIERS.reduce((meilleur, palier) => Math.abs(palier - centimes) < Math.abs(meilleur - centimes) ? palier : meilleur);
}
/**
 * Six prix à proposer autour d'un montant de référence : deux crans en
 * dessous, le palier lui-même, trois au-dessus. Assez pour couvrir l'hésitation
 * du vendeur sans transformer l'écran en catalogue.
 */
export function echellePrix(referenceCentimes) {
    const ancre = palierProche(Math.max(100, referenceCentimes));
    const rang = PALIERS.indexOf(ancre);
    const debut = Math.max(0, Math.min(rang - 2, PALIERS.length - 6));
    return PALIERS.slice(debut, debut + 6);
}
/**
 * Ordre de grandeur habituel d'une famille.
 *
 * Ce ne sont pas des estimations : c'est le **prix médian constaté** dans
 * l'ancien logiciel, famille par famille, sur les 337 127 articles encodés
 * depuis 2010. La première version de ce tableau était inventée et visait bien
 * trop haut — 145 € pour un meuble, là où la médiane réelle du magasin est de
 * 39 €. Un dépôt-vente de quartier n'est pas une salle des ventes.
 *
 * La médiane, et non la moyenne : quelques pièces à 2 000 € tirent la moyenne
 * vers le haut sans rien dire du prix qu'on tape vingt fois par jour.
 */
export const ANCRE_FAMILLE = {
    Décoration: 1_200,
    Ameublement: 3_900,
    Librairie: 300,
    Jouets: 900,
    Collection: 2_000,
    'Articles ménagers': 900,
    Outillage: 700,
    'Cadres & toiles': 2_000,
    'Films DVD': 200,
    Lustrerie: 1_500,
    Vaisselle: 1_200,
    Maroquinerie: 2_000,
    BD: 400,
    Loisirs: 1_200,
    'Jeux vidéo & consoles': 600,
    CD: 300,
    Disques: 500,
    Bijoux: 1_500,
    'Radio / TV / Hi-fi': 2_000,
    Horlogerie: 2_500,
    'Petit électro': 1_500,
    Militaria: 2_500,
    Verrerie: 1_000,
    Maison: 900,
    'Blu-ray': 400,
    Sports: 2_000,
    'Articles professionnels': 2_500,
    Jardin: 1_500,
    Bureau: 900,
    'Auto & moto': 1_500,
    Photo: 1_500,
    'Gros électro': 9_900,
    Musique: 2_000,
    Chauffage: 1_500,
    Informatique: 1_200,
    'Émaux de Longwy': 4_000,
    Noël: 800,
    Tapis: 4_000,
    'Spécial bébé': 1_500,
    Vêtements: 3_500,
    Parfums: 4_500,
    'Bon cadeau': 8_500,
    Divers: 500,
    'Achats divers': 900,
    'Achat jeux vidéo': 500,
    'Achat Blu-ray': 300,
    'Achat livre': 300,
    'Achat BD': 400,
    Achat: 1_800,
    'Achat bijoux': 700,
    'Achat ameublement': 3_000,
    'Achat DVD & jeux': 700,
};
/**
 * Depuis combien de temps une pièce est en rayon, en une expression courte —
 * « 12 j », « 3 mois », « 1 an 2 mois ». Le comptoir a besoin de l'ordre de
 * grandeur, pas du décompte au jour près passé le premier mois.
 */
export function anciennete(deposeLe, maintenant = new Date()) {
    const depot = typeof deposeLe === 'string' ? new Date(deposeLe) : deposeLe;
    if (Number.isNaN(depot.getTime()))
        return '—';
    const jours = Math.max(0, Math.floor((maintenant.getTime() - depot.getTime()) / 86_400_000));
    if (jours === 0)
        return 'aujourd’hui';
    if (jours === 1)
        return 'hier';
    if (jours < 45)
        return `${jours} j`;
    const mois = Math.round(jours / 30.44);
    if (mois < 12)
        return `${mois} mois`;
    const annees = Math.floor(mois / 12);
    const reste = mois % 12;
    return reste === 0 ? `${annees} an${annees > 1 ? 's' : ''}` : `${annees} an${annees > 1 ? 's' : ''} ${reste} mois`;
}
//# sourceMappingURL=prix.js.map